import React from 'react';
import { TableRow, TableCell, Chip, Button } from '@mui/material';
import type { MonthlyPayrollData } from '../../types/employee';
import {
  resolvePayType,
  isDailyWorkerPay,
  isBusinessServicePay,
} from '../../types/employee';
import { payTypeLabel } from '../../lib/payroll/display-helper';
import { formatNumber } from '../../utils/formatters';

const CELL_STYLE = {
  borderRight: '1px solid',
  borderColor: 'divider',
  py: 0.75,
  px: 1,
  verticalAlign: 'middle',
  overflow: 'hidden',
} as const;

interface PayrollDataRowProps {
  data: MonthlyPayrollData;
  muted?: boolean;
  onPayslipClick: (data: MonthlyPayrollData) => void;
}

export const PayrollDataRow: React.FC<PayrollDataRowProps> = ({
  data,
  muted = false,
  onPayslipClick,
}) => {
  const payType = resolvePayType(data.employee.payType, data.employee.isHourly);
  const isNonRegular =
    isDailyWorkerPay(data.employee) || isBusinessServicePay(data.employee);
  const workDays = data.workDays ?? 0;

  const extraPay =
    (data.overtimePay ?? 0) + (data.nightPay ?? 0) + (data.holidayPay ?? 0);
  const extraPayDisplay = extraPay > 0 ? extraPay.toLocaleString('ko-KR') : '-';

  const insuranceLabel = isBusinessServicePay(data.employee)
    ? data.insurance.total
    : isDailyWorkerPay(data.employee)
      ? data.insurance.total
      : data.insurance.total;

  return (
    <TableRow
      hover={!muted}
      sx={{
        opacity: muted ? 0.65 : 1,
        '&:hover': muted ? undefined : { backgroundColor: 'action.hover' },
      }}
    >
      <TableCell sx={CELL_STYLE}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <strong>{data.employee.name}</strong>
          <Chip
            label={payTypeLabel(payType)}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              bgcolor: isNonRegular ? 'var(--bg-green)' : undefined,
            }}
          />
          {data.employee.age != null && (
            <Chip
              label={`${data.employee.age}세`}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {data.employee.isRetired && (
            <Chip label="퇴직" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          )}
        </span>
      </TableCell>
      <TableCell sx={{ ...CELL_STYLE, textAlign: 'right' }}>
        {isNonRegular
          ? workDays > 0
            ? `${workDays}일`
            : '-'
          : data.employee.isHourly
            ? formatNumber(data.totalWorkHours, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              }) + '시간'
            : '-'}
      </TableCell>
      <TableCell sx={{ ...CELL_STYLE, textAlign: 'right' }}>
        {data.baseSalary.toLocaleString('ko-KR')}
      </TableCell>
      <TableCell sx={{ ...CELL_STYLE, textAlign: 'right' }}>
        {isNonRegular ? '-' : data.holidayAllowance > 0
          ? data.holidayAllowance.toLocaleString('ko-KR')
          : '-'}
      </TableCell>
      <TableCell sx={{ ...CELL_STYLE, textAlign: 'right' }}>
        {isNonRegular ? '-' : extraPayDisplay}
      </TableCell>
      <TableCell sx={{ ...CELL_STYLE, textAlign: 'right' }}>
        {data.totalSalary.toLocaleString('ko-KR')}
      </TableCell>
      <TableCell sx={{ ...CELL_STYLE, textAlign: 'right', color: isDailyWorkerPay(data.employee) ? 'var(--c-red-600)' : undefined }}>
        {insuranceLabel.toLocaleString('ko-KR')}
        {isDailyWorkerPay(data.employee) && insuranceLabel > 0 && (
          <span style={{ fontSize: '0.65rem', display: 'block', color: 'var(--fg-tertiary)' }}>
            고용(근로자)
          </span>
        )}
      </TableCell>
      <TableCell sx={{ ...CELL_STYLE, textAlign: 'right' }}>
        {isBusinessServicePay(data.employee) ? '-' : data.tax.withholding.toLocaleString('ko-KR')}
      </TableCell>
      <TableCell sx={{ ...CELL_STYLE, textAlign: 'right' }}>
        {isBusinessServicePay(data.employee) ? '-' : data.tax.local.toLocaleString('ko-KR')}
      </TableCell>
      <TableCell sx={{ ...CELL_STYLE, textAlign: 'right' }}>
        <strong style={{ color: 'var(--primary)' }}>{data.netSalary.toLocaleString('ko-KR')}</strong>
      </TableCell>
      <TableCell sx={CELL_STYLE}>
        {!muted && (
          <Button
            size="small"
            onClick={() => onPayslipClick(data)}
            sx={{
              fontSize: 'clamp(0.65rem, 1.2vw, 0.8125rem)',
              minWidth: 0,
              px: 0.75,
            }}
          >
            명세서
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};
